import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import {
  createQueryParams,
  createShouldClause,
  createTermClause,
  type ElasticsearchQueryFilter,
  INDICES,
  QUERY_DEFAULTS,
} from '@/shared/api/openmetadata';
import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { escapeSearchQuery } from '@/shared/lib/sanitize';
import type { SortOption } from '@/shared/types/common';

import type { FilterState } from '@/entities/discovery/domain/types';

const buildDataAssetsQueryFilter = (domainFqn: string, filters: FilterState): ElasticsearchQueryFilter => {
  const mustClauses: Array<Record<string, unknown>> = [];

  const domainClause = {
    bool: {
      should: [
        { term: { 'domains.fullyQualifiedName': domainFqn } },
        { prefix: { 'domains.fullyQualifiedName': `${domainFqn}.` } },
      ],
    },
  };
  mustClauses.push(domainClause);

  if (filters.assetType && filters.assetType.length > 0) {
    mustClauses.push(createShouldClause('entityType', filters.assetType));
  }

  if (filters.dataSource && filters.dataSource.length > 0) {
    const serviceTypes = filters.dataSource.map((ds) => ds.split('.')[0]);
    mustClauses.push(createShouldClause('serviceType', serviceTypes));
  }

  if (filters.owner && filters.owner.length > 0) {
    mustClauses.push(createShouldClause('owners.displayName.keyword', filters.owner));
  }

  if (filters.tags && filters.tags.length > 0) {
    mustClauses.push(createShouldClause('tags.tagFQN', filters.tags));
  }

  if (filters.tier && filters.tier.length > 0) {
    mustClauses.push(createShouldClause('tier.tagFQN', filters.tier));
  }

  if (filters.certification && filters.certification.length > 0) {
    mustClauses.push(createShouldClause('certification.tagLabel.tagFQN', filters.certification));
  }

  const mustNotClauses = [createTermClause('entityType', 'dataProduct')];

  return {
    query: {
      bool: {
        must: mustClauses,
        must_not: mustNotClauses,
      },
    },
  };
};

export const searchDataAssets = async (
  domainFqn: string,
  filters: FilterState,
  query: string = '',
  from: number = 0,
  size: number = QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
  sortBy: SortOption['field'],
  sortOrder: SortOption['direction']
): Promise<OpenMetadataResponse> => {
  // OpenSearch 특수 문자 이스케이핑 (파싱 에러 방지)
  const escapedQuery = query ? escapeSearchQuery(query) : '';

  const params = createQueryParams({
    q: escapedQuery ? `displayName:*${escapedQuery}* OR description:*${escapedQuery}*` : QUERY_DEFAULTS.Q,
    index: INDICES.DATA_ASSET,
    from,
    size,
    sort_field: sortBy,
    sort_order: sortOrder,
    exclude_source_fields: 'columns',
  });

  const queryFilter = buildDataAssetsQueryFilter(domainFqn, filters);
  params.query_filter = JSON.stringify(queryFilter);

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params,
    paramsSerializer: {
      encode: (value) => encodeURIComponent(value),
    },
  });

  return response.data;
};
